/** Why a draft is not being auto-posted. Mirrors the operations union. */
export type AutoPostRefusal =
  | 'deployment-disabled'
  | 'kill-switch-active'
  | 'not-opted-in'
  | 'opt-in-has-no-actor'
  | 'writes-disabled'
  | 'platform-not-connected'
  | 'draft-is-not-ai'
  | 'not-a-pending-draft'
  | 'cancelled-by-human'
  | 'delay-not-elapsed'
  | 'draft-has-no-timestamp';

export interface AutoPostPendingItem {
  externalReviewId: string;
  platform: string;
  rating: number;
  author: string | null;
  reviewText: string | null;
  reviewPostedAt: string;
  /** The exact string that would be published. Not a summary of it. */
  replyText: string | null;
  draftModel: 'ai' | 'template' | null;
  generatedAt: string | null;
  /** When the delay window closes. Null when there is nothing to measure. */
  postAt: string | null;
  delayMinutes: number;
  willPost: boolean;
  blockedBy: AutoPostRefusal | null;
  approverId: string | null;
  cancelledAt: string | null;
  cancelledBy: string | null;
}

/**
 * Plain-English reasons, in the second person, because this is read by whoever
 * has to decide whether to intervene rather than by whoever wrote the gate.
 */
export const AUTO_POST_REFUSAL_COPY: Record<AutoPostRefusal, string> = {
  // Not the tenant's setting and not something they can undo, so the copy
  // says who to ask rather than implying a switch they can find.
  'deployment-disabled':
    'Automatic replies are switched off for this installation — nothing goes out on its own until your administrator turns it on',
  'kill-switch-active': 'Auto-post is paused for your whole account',
  'not-opted-in': 'Auto-post is off for this platform',
  'opt-in-has-no-actor':
    'Auto-post was switched on without a named user, so there is no one to attribute the reply to',
  'writes-disabled': 'Replies are switched off for this platform',
  'platform-not-connected':
    'This platform is not connected right now — reconnect it in Platforms and this reply goes out on the next check',
  'draft-is-not-ai':
    'This draft came from the fallback template, not the AI writer — templates are never auto-posted',
  'not-a-pending-draft': 'This review already has a reply',
  'cancelled-by-human': 'Someone cancelled this one',
  'delay-not-elapsed': 'Still inside the window where you can step in',
  'draft-has-no-timestamp':
    'This draft has no generation time, so there is no window to measure — it will not go out on its own',
};

/**
 * Whether lifting the stated obstacle would put the draft back on course to
 * publish. This is the question the `Don't post this` button answers, and it
 * is not the same question as "is it going out right now".
 *
 * Most of these obstacles are one click from disappearing, and when one does,
 * the sweep publishes whatever is still queued behind it. So an operator who
 * has read a draft and decided against it has to be able to say so *while it
 * is held*, rather than flip back to this screen afterwards and race an hourly
 * sweep. That is sharpest for `deployment-disabled`: the switch is thrown by
 * an administrator, possibly mid-deploy, with no signal to whoever is reading
 * this page — and the empty state invites them to read it in exactly that
 * state.
 *
 * A `Record` rather than a set of exceptions, so the typecheck makes whoever
 * adds the next refusal decide which kind it is — the same reason
 * `AUTO_POST_REFUSAL_COPY` makes them write the operator's wording.
 */
export const AUTO_POST_REFUSAL_CAN_STILL_PUBLISH: Record<AutoPostRefusal, boolean> = {
  // Switches. Each of these is someone turning something back on, after which
  // this draft goes out on the next sweep.
  'deployment-disabled': true,
  'kill-switch-active': true,
  'not-opted-in': true,
  'opt-in-has-no-actor': true,
  'writes-disabled': true,
  'platform-not-connected': true,
  // Inside its window — already grouped as going out, and always had the button.
  'delay-not-elapsed': true,

  // Facts about this particular draft that no setting changes. Offering to
  // stop these would claim to prevent something that was never going to happen.
  'draft-is-not-ai': false,
  'not-a-pending-draft': false,
  'draft-has-no-timestamp': false,
  // Already stopped by a human, which is what the button does.
  'cancelled-by-human': false,
};

/**
 * The preview response. An envelope rather than a bare array because the
 * screen has to be able to say it is showing only part of the picture — the
 * rows it omits stay eligible for the sweep.
 */
export interface AutoPostPendingResponse {
  items: AutoPostPendingItem[];
  truncated: boolean;
  shown: number;
}
